import { normalizeChatAlertPrefs, type ChatAlertPrefs, type UserPreferences } from '@natashu/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useToast } from '../../components/ui/Toast.tsx';
import { api } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { readStorage, writeStorage } from '../../lib/hooks.ts';

/** GET/PUT /api/me/preferences (apps/server/src/modules/preferences/routes.ts). */
export const PREFS_KEY = ['preferences'] as const;
const STORAGE_KEY = 'natashu.chatAlerts.prefs';

/** Last known settings on this device (used before the API answers, or when it cannot). */
export function cachedChatAlertPrefs(): ChatAlertPrefs {
  return normalizeChatAlertPrefs(readStorage<unknown>(STORAGE_KEY, null));
}

/** Per-user chat alert settings stored on the server (follow the user across devices), cached locally. */
export function useChatAlertPrefs() {
  const { status } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const signedIn = status === 'authenticated';

  const query = useQuery({
    queryKey: PREFS_KEY,
    queryFn: async ({ signal }) => {
      const p = await api.get<UserPreferences>('/api/me/preferences', { signal });
      writeStorage(STORAGE_KEY, p.chatAlerts);
      return p;
    },
    enabled: signedIn,
    staleTime: 5 * 60_000,
  });

  const mutation = useMutation({
    mutationKey: PREFS_KEY,
    mutationFn: (patch: Partial<ChatAlertPrefs>) => api.put<UserPreferences>('/api/me/preferences', { chatAlerts: patch }),
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: PREFS_KEY });
      const prev = queryClient.getQueryData<UserPreferences>(PREFS_KEY);
      const next: UserPreferences = { chatAlerts: { ...(prev?.chatAlerts ?? cachedChatAlertPrefs()), ...patch } };
      queryClient.setQueryData(PREFS_KEY, next);
      writeStorage(STORAGE_KEY, next.chatAlerts);
      return { prev };
    },
    onError: (err, _patch, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(PREFS_KEY, ctx.prev);
        writeStorage(STORAGE_KEY, ctx.prev.chatAlerts);
      }
      toast.error(err);
    },
    onSuccess: (data) => {
      // A later change may still be in flight: keep its optimistic value.
      if (queryClient.isMutating({ mutationKey: PREFS_KEY }) > 1) return;
      queryClient.setQueryData(PREFS_KEY, data);
      writeStorage(STORAGE_KEY, data.chatAlerts);
    },
  });

  const { mutate } = mutation;
  const update = useCallback(
    (patch: Partial<ChatAlertPrefs>) => {
      if (!signedIn) {
        writeStorage(STORAGE_KEY, { ...cachedChatAlertPrefs(), ...patch });
        return;
      }
      mutate(patch);
    },
    [signedIn, mutate],
  );

  return {
    prefs: query.data?.chatAlerts ?? cachedChatAlertPrefs(),
    loaded: query.isSuccess,
    saving: mutation.isPending,
    update,
  };
}
